/**
 * The organisation the person is working in, shared by every screen under it:
 * its API, its people (so a card can say "Ivan" rather than an id), and how
 * many notices are waiting.
 *
 * The unread count is asked for again every minute and whenever the tab comes
 * back into view -- a handoff that arrives while somebody is in another tab
 * should be there when they look, without a socket to keep open.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Member, OrgSummary } from "@utuwrk/core";
import { orgApi, type OrgApi } from "./api.js";

export interface OrgState {
  org: OrgSummary;
  api: OrgApi;
  people: Member[];
  departments: string[];
  /** A person's name, their address when they have none, or "Nobody". */
  nameOf: (id: string | null) => string;
  refreshPeople: () => Promise<void>;
  unread: number;
  refreshUnread: () => Promise<void>;
  /** Ask who is signed in again: the organisation's name comes from there. */
  refreshSession: () => Promise<void>;
}

const OrgContext = createContext<OrgState | null>(null);

export function useOrg(): OrgState {
  const state = useContext(OrgContext);
  if (!state) throw new Error("useOrg() outside an OrgProvider");
  return state;
}

const UNREAD_EVERY_MS = 60_000;

export function OrgProvider({
  org,
  refreshSession,
  children,
}: {
  org: OrgSummary;
  refreshSession: () => Promise<void>;
  children: ReactNode;
}) {
  const api = useMemo(() => orgApi(org.id), [org.id]);
  const [people, setPeople] = useState<Member[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [unread, setUnread] = useState(0);

  const refreshPeople = useCallback(async () => {
    try {
      const answer = await api.people();
      setPeople(answer.people);
      setDepartments(answer.departments);
    } catch {
      // Names fall back to "Somebody"; every screen still works without them.
    }
  }, [api]);

  const refreshUnread = useCallback(async () => {
    try {
      setUnread((await api.notices()).unread);
    } catch {
      // A missed count is caught up on the next tick.
    }
  }, [api]);

  useEffect(() => {
    void refreshPeople();
    void refreshUnread();
    // Only while it is on screen: the asking is also how the server knows
    // somebody is here and need not be mailed, so a hidden tab must not.
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refreshUnread();
    }, UNREAD_EVERY_MS);
    const visible = () => {
      if (document.visibilityState === "visible") void refreshUnread();
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [refreshPeople, refreshUnread]);

  const nameOf = useCallback(
    (id: string | null) => {
      if (!id) return "Nobody";
      if (id === "system") return "utuwrk";
      const person = people.find((candidate) => candidate.id === id);
      return person ? person.name || person.email : "Somebody";
    },
    [people],
  );

  const value = useMemo(
    () => ({ org, api, people, departments, nameOf, refreshPeople, unread, refreshUnread, refreshSession }),
    [org, api, people, departments, nameOf, refreshPeople, unread, refreshUnread, refreshSession],
  );
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
